import React, { useEffect, useState } from "react";
import { fetchClients } from "@/lib/api";
import { PageHero, Section, SectionHead } from "@/components/Section";

// Brand evolution milestones — public history of Gedore (DE) and Jhalani (IN).
const TIMELINE = [
  {
    year: "1919",
    title: "Gedore is founded in Germany",
    body:
      "The Dowidat brothers establish Gedore in Remscheid, Germany — a forge that would become one of Europe's most respected hand-tool makers, especially for spanners, sockets and wrenches.",
    tag: "Gedore · DE",
  },
  {
    year: "1961",
    title: "Jhalani brings Gedore to India",
    body:
      "The Jhalani family launches Indian production under technical collaboration with Gedore — bringing German drop-forging metallurgy, dimensional discipline and bi-hex socket geometry to Indian workshops for the first time. Spanners roll out under the co-branding ‘Gedore Jhalani’.",
    tag: "Gedore Jhalani",
  },
  {
    year: "1990s",
    title: "Export markets open up",
    body:
      "Jhalani-built tools begin shipping to industrial buyers across the Middle East, Africa, Southeast Asia and Europe — under both private labels and the Jhalani name.",
    tag: "Global Exports",
  },
  {
    year: "2010s",
    title: "Brand consolidates as Jhalani Hand Tools",
    body:
      "As the partnership with Gedore is wound down, the Indian operation continues independently as Jhalani Hand Tools — retaining the original forging plant, the original quality programme, and the original family ownership.",
    tag: "Jhalani Hand Tools",
  },
  {
    year: "2015",
    title: "Ferreterro Tools LLP becomes the parent entity",
    body:
      "The business is restructured under Ferreterro Tools LLP, the holding entity that today manufactures and distributes the full Jhalani range — spanners, sockets, pliers, screwdrivers, hammers and more — across India and a growing list of international markets.",
    tag: "Ferreterro Tools LLP",
  },
  {
    year: "2015 – 2024",
    title: "Deepening roots in the Indian market",
    body:
      "Through the decade that follows, Jhalani builds out its domestic distribution — opening up new dealer territories, expanding its presence with OEMs, refineries, railways and infrastructure majors, and earning a place on the workbench of India's industrial backbone.",
    tag: "India · Domestic Growth",
  },
  {
    year: "2025",
    title: "Cold-forged sockets, made in India",
    body:
      "Jhalani commissions India's first commercial cold-forging line for impact sockets — moving the country off cast sockets and onto tools that hold torque the way OEM buyers demand.",
    tag: "Made in India",
  },
];

// Local logos curated for each client. If a file isn't present, we fall back to a typographic tile.
const CLIENT_LOGO_FILE = {
  Abb: "abb.svg",
  Hp: "hp.svg",
  "Indian Oil": "indian-oil.svg",
  "Indian Railways": "indian-railways.svg",
  Ntpc: "ntpc.svg",
  Tata: "tata.svg",
  Beml: "beml.svg",
  Putsmeister: "putzmeister.png",
};

const DISPLAY_NAME = {
  "Acc Limited": "ACC",
  Hp: "HP",
  Beml: "BEML",
  Ntpc: "NTPC",
  Ongc: "ONGC",
  Sail: "SAIL",
  "Larsen Turbo": "Larsen & Toubro",
  Putsmeister: "Putzmeister",
  "Relience Industry": "Reliance Industries",
};

const ClientLogoTile = ({ name }) => {
  const [errored, setErrored] = useState(false);
  const file = CLIENT_LOGO_FILE[name];
  const label = DISPLAY_NAME[name] || name;
  return (
    <div className="bg-white aspect-[4/3] flex items-center justify-center p-5">
      {file && !errored ? (
        <img
          src={`/clients/${file}`}
          alt={`${label} logo`}
          loading="lazy"
          onError={() => setErrored(true)}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="font-display font-bold text-sm uppercase tracking-wider text-[var(--jt-ink)] text-center">
          {label}
        </span>
      )}
    </div>
  );
};

const About = () => {
  const [clients, setClients] = useState([]);
  useEffect(() => {
    fetchClients().then(setClients);
  }, []);

  return (
    <>
      <PageHero
        eyebrow="Our Legacy"
        title="A legacy forged in chrome-vanadium"
        intro="The hand-tool brand India's industrial professionals have trusted since 1961 — born from a partnership with German engineering, grown in India, exported worldwide."
        breadcrumbs={<>Home / <span className="text-white">Our Legacy</span></>}
      />

      {/* ---------- BRAND EVOLUTION TIMELINE (Gedore -> Jhalani -> Ferreterro) ---------- */}
      <Section>
        <SectionHead
          eyebrow="1919 → Today"
          title="From Remscheid to India, and on to the world"
          intro="The Jhalani story didn't start in 1961 — it started in 1919 in a German forge, and travelled to India through one of the country's most enduring industrial collaborations."
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
          {/* Vertical timeline */}
          <div className="lg:col-span-8 relative" data-testid="legacy-timeline">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-[var(--jt-border)]" aria-hidden="true" />
            <ol className="space-y-10">
              {TIMELINE.map((t) => (
                <li key={t.year} className="relative pl-12">
                  <span className="absolute left-0 top-1 w-8 h-8 border-2 border-[var(--jt-primary)] bg-white flex items-center justify-center">
                    <span className="w-2.5 h-2.5 bg-[var(--jt-primary)]" />
                  </span>
                  <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-[var(--jt-primary)]">
                    {t.year} · {t.tag}
                  </div>
                  <h3 className="mt-1 font-display font-bold tracking-tight text-2xl lg:text-3xl leading-tight">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-[var(--jt-text-muted)] leading-relaxed max-w-2xl">{t.body}</p>
                </li>
              ))}
            </ol>
          </div>

          {/* Side card — at-a-glance */}
          <aside className="lg:col-span-4">
            <div className="bg-[var(--jt-ink)] text-white p-7 sticky top-32">
              <div className="eyebrow !text-[var(--jt-primary)]">At a Glance</div>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex justify-between border-b border-white/10 pb-2"><span>Indian operation</span><span className="font-mono">Est. 1961</span></li>
                <li className="flex justify-between border-b border-white/10 pb-2"><span>German lineage</span><span className="font-mono">Gedore · 1919</span></li>
                <li className="flex justify-between border-b border-white/10 pb-2"><span>HQ</span><span className="font-mono">Noida, IN</span></li>
                <li className="flex justify-between border-b border-white/10 pb-2"><span>Categories</span><span className="font-mono">22</span></li>
                <li className="flex justify-between border-b border-white/10 pb-2"><span>SKUs</span><span className="font-mono">100+</span></li>
                <li className="flex justify-between"><span>Exporting</span><span className="font-mono">Worldwide</span></li>
              </ul>
              <div className="mt-6 pt-5 border-t border-white/10">
                <div className="font-mono text-[0.6rem] tracking-[0.25em] uppercase text-white/40">Today operating as</div>
                <div className="mt-1 font-display font-bold text-lg">Jhalani Hand Tools</div>
                <div className="font-mono text-[0.65rem] tracking-widest uppercase text-white/60 mt-0.5">A Ferreterro Tools brand</div>
              </div>
            </div>
          </aside>
        </div>
      </Section>

      {/* ---------- INDUSTRY TRUST (moved from Home) — real client logos ---------- */}
      {clients.length ? (
        <Section dark>
          <SectionHead
            dark
            eyebrow="Industry Trust"
            title="Specified into India's biggest projects"
            intro="From cement plants to railways, refineries to defence — Jhalani tools have been part of India's infrastructure backbone for decades."
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/10 border border-white/10" data-testid="legacy-clients-grid">
            {clients.map((c) => (
              <ClientLogoTile key={c.id} name={c.name} />
            ))}
          </div>
          <p className="mt-6 font-mono text-[0.65rem] tracking-widest uppercase text-white/40 text-center">
            Logos belong to their respective owners and indicate supply / use, not endorsement.
          </p>
        </Section>
      ) : null}
    </>
  );
};

export default About;
